import { CookieBanner } from "@/components/cookie-banner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { Route, Switch } from "wouter";
import { queryClient } from "./lib/queryClient";

// Lazy load pages for better code splitting
const Home = lazy(() => import("@/pages/home"));
const Clinic = lazy(() => import("@/pages/clinic"));
const Specialists = lazy(() => import("@/pages/specialists"));
const ServicesPage = lazy(() => import("@/pages/services"));
const ExternalConsultation = lazy(
  () => import("@/pages/external-consultation"),
);
const ClinicalLaboratory = lazy(() => import("@/pages/clinical-laboratory"));
const MedicalSpecialties = lazy(() => import("@/pages/medical-specialties"));
const Imagenologia = lazy(() => import("@/pages/imagenologia"));
const CentroDigestivo = lazy(() => import("@/pages/centro-digestivo"));
const Emergencia = lazy(() => import("@/pages/emergencia"));
const Hospitalizacion = lazy(() => import("@/pages/hospitalizacion"));
const AreaQuirurgica = lazy(() => import("@/pages/area-quirurgica"));
const Neonatologia = lazy(() => import("@/pages/neonatologia"));
const UCI = lazy(() => import("@/pages/uci"));
const AgendarCita = lazy(() => import("@/pages/agendar-cita"));
const SpecialtyDetail = lazy(() => import("@/pages/specialty-detail"));
const LabQuoter = lazy(() => import("@/pages/lab-quoter"));
const ServiceDetail = lazy(() => import("@/pages/service-details"));
const Patients = lazy(() => import("@/pages/patients"));
const Medilife = lazy(() => import("@/pages/medilife"));
const FormasPago = lazy(() => import("@/pages/formas-pago"));
const News = lazy(() => import("@/pages/news"));
const NewsPost = lazy(() => import("@/pages/news-post"));
const Gallery = lazy(() => import("@/pages/gallery"));
const Convenios = lazy(() => import("@/pages/convenios"));
const Aseguradoras = lazy(() => import("@/pages/aseguradoras"));
const Careers = lazy(() => import("@/pages/careers"));
const Contact = lazy(() => import("@/pages/contact"));
const DoctorDetails = lazy(() => import("@/pages/doctor-details"));
const BlogPost = lazy(() => import("@/pages/blog-post"));

// Admin pages
const AdminLogin = lazy(() => import("@/pages/admin-login"));
const AdminDashboard = lazy(() => import("@/pages/admin-dashboard"));
const AdminDoctors = lazy(() => import("@/pages/admin-doctors"));
const AdminServices = lazy(() => import("@/pages/admin-services"));
const AdminNews = lazy(() => import("@/pages/admin-news"));
const AdminJobs = lazy(() => import("@/pages/admin-jobs"));
const AdminInsurers = lazy(() => import("@/pages/admin-insurers"));
const AdminLabTests = lazy(() => import("@/pages/admin-lab-tests"));
const AdminMembershipPlans = lazy(
  () => import("@/pages/admin-membership-plans"),
);
const AdminMedilifeApplications = lazy(
  () => import("@/pages/admin-medilife-applications"),
);
const AdminJobApplications = lazy(
  () => import("@/pages/admin-job-applications"),
);
const AdminNewsletter = lazy(() => import("@/pages/admin-newsletter"));
const AdminContactMessages = lazy(
  () => import("@/pages/admin-contact-messages"),
);
const AdminUsers = lazy(() => import("@/pages/admin-users"));
const AdminCampaigns = lazy(() => import("@/pages/admin-campaigns"));
const AdminSpecialties = lazy(() => import("@/pages/admin-specialties"));
const AdminBlog = lazy(() => import("@/pages/admin-blog"));
const AdminGallery = lazy(() => import("@/pages/admin-gallery"));

// Legal pages
const Privacy = lazy(() => import("@/pages/privacy"));
const Terms = lazy(() => import("@/pages/terms"));
const CookiePolicy = lazy(() => import("@/pages/cookie-policy"));
const NotFound = lazy(() => import("@/pages/not-found"));

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/clinica" component={Clinic} />
      <Route path="/especialistas" component={Specialists} />
      <Route path="/servicios" component={ServicesPage} />
      <Route
        path="/servicios/consulta-externa"
        component={ExternalConsultation}
      />
      <Route
        path="/servicios/laboratorio-clinico"
        component={ClinicalLaboratory}
      />
      <Route path="/servicios/especialidades" component={MedicalSpecialties} />
      <Route path="/servicios/imagenologia" component={Imagenologia} />
      <Route
        path="/servicios/enfermedades-digestivas"
        component={CentroDigestivo}
      />
      <Route path="/servicios/emergencia" component={Emergencia} />
      <Route path="/servicios/hospitalizacion" component={Hospitalizacion} />
      <Route path="/servicios/area-quirurgica" component={AreaQuirurgica} />
      <Route path="/servicios/neonatologia" component={Neonatologia} />
      <Route path="/servicios/uci" component={UCI} />
      <Route path="/agendar-cita" component={AgendarCita} />
      <Route path="/especialidades/:slug" component={SpecialtyDetail} />
      <Route path="/cotizador-laboratorio" component={LabQuoter} />
      <Route path="/servicios/:id" component={ServiceDetail} />
      <Route path="/pacientes" component={Patients} />
      <Route path="/medilife" component={Medilife} />
      <Route path="/formas-de-pago" component={FormasPago} />
      <Route path="/noticias" component={News} />
      <Route path="/noticias/:id" component={NewsPost} />
      <Route path="/galeria" component={Gallery} />
      <Route path="/convenios" component={Convenios} />
      <Route path="/aseguradoras" component={Aseguradoras} />
      <Route path="/trabaja-con-nosotros" component={Careers} />
      <Route path="/contacto" component={Contact} />
      <Route path="/doctores/:id" component={DoctorDetails} />
      <Route path="/blog/:slug" component={BlogPost} />

      {/* Admin Routes */}
      <Route path="/admin/login" component={AdminLogin} />
      <Route path="/admin" component={AdminDashboard} />
      <Route path="/admin/doctors" component={AdminDoctors} />
      <Route path="/admin/services" component={AdminServices} />
      <Route path="/admin/news" component={AdminNews} />
      <Route path="/admin/jobs" component={AdminJobs} />
      <Route path="/admin/insurers" component={AdminInsurers} />
      <Route path="/admin/lab-tests" component={AdminLabTests} />
      <Route path="/admin/membership-plans" component={AdminMembershipPlans} />
      <Route
        path="/admin/medilife-applications"
        component={AdminMedilifeApplications}
      />
      <Route path="/admin/job-applications" component={AdminJobApplications} />
      <Route path="/admin/newsletter" component={AdminNewsletter} />
      <Route path="/admin/contact-messages" component={AdminContactMessages} />
      <Route path="/admin/users" component={AdminUsers} />
      <Route path="/admin/campaigns" component={AdminCampaigns} />
      <Route path="/admin/specialties" component={AdminSpecialties} />
      <Route path="/admin/blog" component={AdminBlog} />
      <Route path="/admin/gallery" component={AdminGallery} />

      {/* Legal Pages */}
      <Route path="/privacidad" component={Privacy} />
      <Route path="/terminos" component={Terms} />
      <Route path="/politica-cookies" component={CookiePolicy} />

      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Suspense
          fallback={
            <div className="flex items-center justify-center min-h-screen">
              Cargando...
            </div>
          }
        >
          <Router />
        </Suspense>
        <CookieBanner />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
